import { capitalize } from 'lodash';
import moment from 'moment';

import { Discount, OrderItem, PaymentMethod } from './types';

// Calculates current year, list of years and list of months
export const year = new Date().getFullYear();
export const years = Array.from(new Array(21), (val, index) => index + year);
export const months = Array.from(Array(12).keys(), (val, index) => index + 1);

// Calculates the expiration date based on duration days and duration months
export const calculateExpirationDate = (days, months) => {
  return moment().add(days, 'd').add(months, 'M').format('LL');
};

// Calculates subtotal price
export const calculateSubtotal = (price, ammount) => {
  return price * ammount;
};

// Sums prices
export const calculateSum = (arr) => {
  return arr.reduce((sum, x) => sum + x);
};

// Calculates price of each installment
export const calculateInstallmentsPrice = (price) => {
  return price / 4;
};

// Calculates installments payment date
export const calculateInstallmentsDates = (interval: 'week' | 'month', intervalCount: number) => {
  const dates = [
    'today',
    moment().add(intervalCount, interval).format('MM/DD'),
    moment().add(intervalCount * 2, interval).format('MM/DD'),
    moment().add(intervalCount * 3, interval).format('MM/DD')
  ];

  return dates;
};

export const isPaidOrder = (discount: Discount, orderItems: OrderItem[]) => {
  if (discount) {
    return discount.discountedPrice > 0;
  } else {
    const totalPrice = orderItems.reduce((total, item) => {
      return total + item.priceCentsPerItem * item.purchasableCount;
    }, 0);
    return totalPrice > 0;
  }
};

export const isPaidCardOrder = (discount: Discount, orderItems: OrderItem[], paymentMethod: PaymentMethod) => {
  if (paymentMethod !== PaymentMethod.CREDITCARD && paymentMethod !== PaymentMethod.STRIPE) {
    return false;
  }
  return isPaidOrder(discount, orderItems);
};

export const pricingComponents = (values) => {
  let components = [];

  if (!values.discount) {
    components.push({
      name: 'Price',
      priceInCents: values.orderItems[0].purchasable.priceCents
    });
    return components
  }

  // Strip out the impact of the default coupon from the UI
  let fullPriceIncludingDefaultCoupon = values.discount.fullPrice;
  if (values.discount.defaultCoupon) {
    fullPriceIncludingDefaultCoupon -= values.discount.defaultCoupon.discountAmount;
  }

  if (values.discount.compoundPrice) {
    values.discount.compoundPrice.forEach((price) => (
      components.push({
        name: price.name,
        priceInCents: price.fullPrice || price.price
      })
    ));
  } else {
    components.push({
      name: 'Price',
      priceInCents: fullPriceIncludingDefaultCoupon
    });
  }

  if (values.discount.discountedPrice !== fullPriceIncludingDefaultCoupon) {
    // Strip out the default coupon from the UI
    const defaultCouponDescriptionRegex = new RegExp(`code "${values.discount.defaultCoupon?.couponCode}"( and |, )`, 'i');
    const descriptionWithoutDefaultCoupon = values.discount.discountDescription.replace(defaultCouponDescriptionRegex, '');

    components.push({
      name: descriptionWithoutDefaultCoupon.replace(/^code/, 'Code').replace(/^upgrade/, 'Upgrade'),
      priceInCents: (fullPriceIncludingDefaultCoupon - values.discount.discountedPrice) * -1
    })
  }

  return components;
};

export const shouldValidatePaymentInfo = (discount, orderItems, paymentMethod, schema) => {
  if (isPaidCardOrder(discount, orderItems, paymentMethod)) {
    return schema.required();
  } else {
    return schema.optional();
  }
};
